import React, { useEffect, useMemo, useState } from 'react';
import { ClipboardPaste, X, Copy } from 'lucide-react';
import { cursiveStyles, cursiveFilters } from '../../data/styles';
import { applyCase } from '../../lib/unicode';
import { copyText, loadHistory, pushHistory, textStats } from '../../lib/helpers';
import { Slider } from '../ui/slider';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import ToolExtras from './ToolExtras';
import ResultsPanel from './ResultsPanel';
import { useConsent } from '../consent/ConsentProvider';

const MAX = 600;

export default function CursiveTool({ t, initialStyle = 'cursive-bold' }) {
  const { preferencesAllowed } = useConsent();
  const [input, setRawInput] = useState('');
  const setInput = (value) => setRawInput(Array.from(value).slice(0, MAX).join(''));
  const [size, setSize] = useState(18);
  const [sort, setSort] = useState('default');
  const [caseMode, setCaseMode] = useState('none');
  const [filter, setFilter] = useState('all');
  const [activeId, setActiveId] = useState(initialStyle);
  const [platform, setPlatform] = useState('Instagram');
  const [history, setHistory] = useState([]);
  useEffect(() => { setHistory(loadHistory()); }, [preferencesAllowed]);

  const cased = Array.from(applyCase(input, caseMode)).slice(0, MAX).join('');
  const styles = useMemo(() => {
    let list = [...cursiveStyles];
    if (sort === 'name') list.sort((a, b) => a.name.localeCompare(b.name));
    return list;
  }, [sort]);
  const shown = filter === 'all' ? styles : styles.filter((s) => s.cat === filter);
  const active = cursiveStyles.find((s) => s.id === activeId);
  const output = cased ? active.fn(cased) : '';
  const stats = textStats(input);

  const doCopy = async (text, styleName) => {
    if (!text || !await copyText(text, t.copied)) return false;
    setHistory(pushHistory({ text, style: styleName, at: Date.now() }));
    return true;
  };

  const handlePaste = async () => {
    try {
      const txt = await navigator.clipboard.readText();
      setInput(txt || '');
    } catch (e) { /* permission denied */ }
  };

  return (
    <div className="tool max-w-[752px] mx-auto space-y-3" data-testid="cursive-tool">
      {/* Controls bar */}
      <div className="tool-card cream px-3.5 py-2.5 flex flex-wrap items-center gap-x-4 gap-y-2" data-testid="controls-bar">
        <div className="flex items-center gap-2.5">
          <span className="ctrl-label">{t.size}</span>
          <Slider aria-label={t.size} value={[size]} min={13} max={34} step={1} onValueChange={(v) => setSize(v[0])} className="w-[104px]" data-testid="font-size-slider" />
          <span className="text-[11px] text-[var(--ink3)] w-8">{size}px</span>
        </div>
        <span className="w-px h-4 bg-[var(--line)]" />
        <div className="flex items-center gap-2">
          <span className="ctrl-label">{t.sort}</span>
          <Select value={sort} onValueChange={setSort}>
            <SelectTrigger aria-label={t.sort} className="h-9 w-[118px] text-[13px] bg-white border-[var(--line)]" data-testid="sort-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="default">{t.sortDefault}</SelectItem>
              <SelectItem value="name">A – Z</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <span className="w-px h-4 bg-[var(--line)]" />
        <div className="flex items-center gap-2">
          <span className="ctrl-label">{t.case}</span>
          <Select value={caseMode} onValueChange={setCaseMode}>
            <SelectTrigger aria-label={t.case} className="h-9 w-[104px] text-[13px] bg-white border-[var(--line)]" data-testid="case-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">{t.asTyped}</SelectItem>
              <SelectItem value="upper">UPPER</SelectItem>
              <SelectItem value="lower">lower</SelectItem>
              <SelectItem value="title">Title</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="ml-auto flex items-center gap-4 text-[11.5px] text-[var(--ink4)]">
          <span>{t.chars}: <b className="text-[var(--gold)]" data-testid="char-count">{stats.chars}</b></span>
          <span>{t.words}: <b className="text-[var(--gold)]" data-testid="word-count">{stats.words}</b></span>
        </div>
      </div>

      {/* Editor */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="tool-card overflow-hidden flex flex-col">
          <div className="flex items-center justify-between px-3 py-2 border-b border-[var(--line)] bg-[var(--paper2)]">
            <span className="upper-label">{t.yourText}</span>
            <div className="flex gap-1.5">
              <button className="sm-btn inline-flex items-center gap-1" onClick={handlePaste} data-testid="paste-btn"><ClipboardPaste className="w-3 h-3" />{t.paste}</button>
              <button className="sm-btn inline-flex items-center gap-1" onClick={() => setInput('')} data-testid="clear-btn"><X className="w-3 h-3" />{t.clear}</button>
            </div>
          </div>
          <textarea
            aria-label={t.yourText}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder={t.placeholder}
            spellCheck={false}
            className="w-full h-[138px] resize-y bg-white px-3.5 py-3 text-[15px] text-[var(--ink)] placeholder:text-[var(--ink4)]"
            data-testid="input-textarea"
          />
          <div className="text-right text-[10.5px] text-[var(--ink4)] px-3 py-1.5 border-t border-[var(--line)] bg-[var(--paper2)] italic">
            <span data-testid="chars-left">{MAX - Array.from(input).length}</span> {t.left}
          </div>
        </div>
        <div className="tool-card overflow-hidden flex flex-col">
          <div className="flex items-center justify-between px-3 py-2 border-b border-[var(--line)] bg-[var(--paper2)]">
            <span className="upper-label">{t.output}</span>
            <button className="sm-btn accent inline-flex items-center gap-1" onClick={() => doCopy(output, active.name)} data-testid="main-copy-btn"><Copy className="w-3 h-3" />{t.copy}</button>
          </div>
          <textarea
            aria-label={t.output}
            readOnly value={output} placeholder={t.outPlaceholder}
            style={{ fontSize: size }}
            className="w-full flex-1 min-h-[160px] resize-y bg-white px-3.5 py-3 text-[var(--ink)] placeholder:text-[var(--ink4)]"
            data-testid="output-textarea"
          />
        </div>
      </div>

      {/* Style selector */}
      <div className="tool-card px-3.5 py-3.5" data-testid="style-selector">
        <div className="flex items-center justify-between mb-3">
          <span className="upper-label flex items-center gap-2"><span className="w-4 h-px bg-[var(--gold2)]" />{t.choose}</span>
          <span className="text-[11.5px] text-[var(--ink4)]" data-testid="style-count">{shown.length} {t.of} {cursiveStyles.length} {t.styles}</span>
        </div>
        <div className="flex flex-wrap gap-1.5 mb-3">
          {cursiveFilters.map((f) => (
            <button key={f.id} className={`pill-btn ${filter === f.id ? 'active' : ''}`} onClick={() => setFilter(f.id)} data-testid={`filter-${f.id}`}>
              {t.filters[f.id]}
            </button>
          ))}
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2">
          {shown.map((s) => (
            <button type="button" aria-pressed={activeId === s.id} key={s.id} className={`spill ${activeId === s.id ? 'active' : ''}`} onClick={() => setActiveId(s.id)} data-testid={`style-${s.id}`}>
              <div className="spill-name">{s.name}</div>
              <div className="spill-prev">{s.fn(cased || t.sample)}</div>
              {s.badge && <span className={s.badge === 'POP' ? 'badge-pop' : 'badge-new'}>{s.badge}</span>}
            </button>
          ))}
        </div>
      </div>

      <ToolExtras t={t} input={input} setInput={setInput} output={output} stats={stats} platform={platform} setPlatform={setPlatform} />

      <ResultsPanel t={t} text={cased} size={size} styles={styles} activeStyle={active} output={output}
        history={history} setHistory={setHistory} onCopy={doCopy} />
    </div>
  );
}
